import React, { useState, useRef, useCallback, useEffect } from "react";
import styles from "./style.module.scss";
import { ButtonPrimary, CopyElement } from "@components";
import { ReactComponent as SampleIcon } from "../HumanizerInput/assets/svg/Sample.svg";
import { ReactComponent as CopyIcon } from "../HumanizerInput/assets/svg/Copy.svg";
import { Action } from "../HumanizerInput/components";
import { useTranslation } from "react-i18next";
import api from '../../http/axios';

const SAMPLE_TEXT = "This is a sample text for summarization. You can use this to test the summarizer functionality. Replace this with your own text to get a quick summary of your content. The summarizer will process your text and provide you with a concise version that captures the main points and key information.";
const MAX_WORDS = 2000;
const MAX_CHARACTERS = 12000; // Backend limit - roughly 2000 words

const countWords = (str: string): number => {
    str = str.trim();
    if (!str) return 0;
    return str.split(/\s+/).length;
};

export const SummarizerInput: React.FC<React.HTMLAttributes<HTMLDivElement>> = (props) => {
    const { t } = useTranslation();
    
    // --- Constants & Refs for resize functionality ---
    const minHeight = 300;
    const maxHeight = 700;
    const defHeight = 350;
    
    const inputTextareaRef = useRef<HTMLTextAreaElement>(null);
    const outputTextareaRef = useRef<HTMLTextAreaElement>(null);
    const resizeHandleRef = useRef<HTMLDivElement>(null);
    const dragInfoRef = useRef<{ startY: number; startHeight: number } | null>(null);
    
    // --- State Variables ---
    const [inputValue, setInputValue] = useState('');
    const [summaryText, setSummaryText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [textareaHeight, setTextareaHeight] = useState(minHeight);
    const [isDragging, setIsDragging] = useState(false);
    const [manuallyResized, setManuallyResized] = useState(false);

    // --- RESIZE HANDLE LOGIC ---
    const handleMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragInfoRef.current = { startY: e.clientY, startHeight: textareaHeight };
            setIsDragging(true);
            setManuallyResized(true);
        },
        [textareaHeight],
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent) => {
            if (!isDragging || !dragInfoRef.current) return;
            const deltaY = e.clientY - dragInfoRef.current.startY;
            const newHeight = dragInfoRef.current.startHeight + deltaY;
            const clampedHeight = Math.min(Math.max(newHeight, minHeight), maxHeight);
            setTextareaHeight(clampedHeight);
            if (inputTextareaRef.current) inputTextareaRef.current.style.height = `${clampedHeight}px`;
            if (outputTextareaRef.current) outputTextareaRef.current.style.height = `${clampedHeight}px`;
        },
        [isDragging, minHeight, maxHeight],
    );

    const handleMouseUp = useCallback(() => {
        if (isDragging) {
            setIsDragging(false);
            dragInfoRef.current = null;
        }
    }, [isDragging]);

    useEffect(() => {
        // Global listeners for drag
        if (isDragging) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "ns-resize";
            document.body.style.userSelect = "none";
        } else {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        }
        return () => {
            // Cleanup
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.body.style.cursor = "";
            document.body.style.userSelect = "";
        };
    }, [isDragging, handleMouseMove, handleMouseUp]);

    const handleSummarize = async () => {
        if (!inputValue.trim()) {
            setError('Please enter text to summarize.');
            return;
        }
        
        const wordCount = countWords(inputValue);
        const charCount = inputValue.length;
        
        if (wordCount > MAX_WORDS) {
            setError(`Text is too long. Maximum ${MAX_WORDS} words allowed.`);
            return;
        }
        
        if (charCount > MAX_CHARACTERS) {
            setError(`Text is too long. Maximum ${MAX_CHARACTERS} characters allowed.`);
            return;
        }
        
        setIsLoading(true);
        setError('');
        setSummaryText('');
        try {
            const response = await api.post('/free-tools/summarize', { text: inputValue });
            setSummaryText(response.data.summary);
        } catch (e: any) {
            if (e.response?.data?.message) {
                // Handle validation errors from backend
                const errorMessage = Array.isArray(e.response.data.message) 
                    ? e.response.data.message.join(', ')
                    : e.response.data.message;
                setError(errorMessage);
            } else {
                setError('An error occurred while summarizing the text.');
            }
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        if (summaryText) {
            navigator.clipboard.writeText(summaryText);
        }
    };

    const textAreaChangeHandler = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setInputValue(value);
        setError('');
        
        // Auto-resize logic
        if (inputTextareaRef.current && !manuallyResized) {
            inputTextareaRef.current.style.height = "auto";
            const scrollHeight = inputTextareaRef.current.scrollHeight;
            const newHeight = Math.min(Math.max(scrollHeight, minHeight), defHeight);
            inputTextareaRef.current.style.height = `${newHeight}px`;
            setTextareaHeight(newHeight);
            if (outputTextareaRef.current) outputTextareaRef.current.style.height = `${newHeight}px`;
        } else if (outputTextareaRef.current && inputTextareaRef.current) {
            outputTextareaRef.current.style.height = inputTextareaRef.current.style.height;
        }
        if (manuallyResized) {
            setManuallyResized(false);
        }
    };

    const pasteActionClickHandler = async () => {
        try {
            const text = await navigator.clipboard.readText();
            setInputValue(text);
            if (inputTextareaRef.current) {
                inputTextareaRef.current.focus();
                setManuallyResized(false);
                requestAnimationFrame(() => {
                    if (inputTextareaRef.current) {
                        textAreaChangeHandler({
                            target: inputTextareaRef.current,
                        } as React.ChangeEvent<HTMLTextAreaElement>);
                    }
                });
            }
        } catch (err) {
            console.error('Failed to read clipboard contents: ', err);
        }
    };

    const tryASampleClickHandler = () => {
        setInputValue(SAMPLE_TEXT);
        if (inputTextareaRef.current) {
            inputTextareaRef.current.focus();
            setManuallyResized(false);
            requestAnimationFrame(() => {
                if (inputTextareaRef.current) {
                    textAreaChangeHandler({
                        target: inputTextareaRef.current,
                    } as React.ChangeEvent<HTMLTextAreaElement>);
                }
            });
        }
    };

    const renderActions = () => {
        if (inputValue.trim().length > 0) return null;
        
        return (
            <div className={styles.ActionWrapper}>
                <div className={styles.ActionWrapper__container}>
                    <Action
                        text={t('summarizer.actions.trySample')}
                        icon={<SampleIcon />}
                        clickHandler={tryASampleClickHandler}
                    />
                    <Action
                        text={t('summarizer.actions.pasteText')}
                        icon={<CopyIcon />}
                        clickHandler={pasteActionClickHandler}
                    />
                </div>
            </div>
        );
    };

    return (
        <div {...props} className={`${styles.SummarizerInput} ${props.className || ""}`}>
            <div className={styles.SummarizerInput__flexWrapper}>
                {/* Input Section */}
                <div className={styles.SummarizerInput__input}>
                    <div className={styles.SummarizerInput__topBar}>
                        <h4 className={styles.SummarizerInput__title}>
                            {t('summarizer.yourText')}
                        </h4>
                        <p className={styles.SummarizerInput__wordCountInfo}>
                            <span>{countWords(inputValue)}</span> / {MAX_WORDS} words
                        </p>
                    </div>
                    
                    <div className={styles.SummarizerInput__fieldWrapper}>
                        <textarea
                            ref={inputTextareaRef}
                            className={styles.SummarizerInput__field}
                            value={inputValue}
                            onChange={textAreaChangeHandler}
                            placeholder={t('summarizer.inputTextPlaceholder')}
                            style={{
								height: `${textareaHeight}px`,
								minHeight: `${minHeight}px`,
								maxHeight: `${maxHeight}px`,
							}}
                        />
                        {renderActions()}
                    </div>
                    
                    <div className={styles.buttons}>
                        <div className={styles.leftContent}>
                            {/* Empty for now */}
                        </div>
                        <div className={styles.rightContent}>
                             <ButtonPrimary
                                onClick={handleSummarize}
                                disabled={isLoading}
                                colorScheme="orange"
                            >
                                {isLoading ? t('summarizer.summarizingButton') : t('summarizer.summarizeButton')}
                            </ButtonPrimary>
                        </div>
                    </div>
                </div>

                {/* Result Section */}
                <div className={styles.SummarizerInput__result}>
                    <div className={styles.SummarizerInput__topBar}>
                        <h4 className={styles.SummarizerInput__title}>
                            {t('summarizer.summaryResult')}
                        </h4>
                        <CopyElement 
                            className={`${!summaryText || summaryText.length === 0 ? styles.copyDisabled : ""}`}
                            onClick={handleCopy} 
                        />
                    </div>

                    <div className={styles.SummarizerInput__fieldWrapper}>
                        <textarea
                            ref={outputTextareaRef}
                            className={styles.SummarizerInput__result_text}
                            value={summaryText}
                            readOnly
                            placeholder={isLoading ? t('humanizer.processing') : t('summarizer.summaryPlaceholder')}
                            style={{
								height: `${textareaHeight}px`,
								minHeight: `${minHeight}px`,
								maxHeight: `${maxHeight}px`,
							}}
                        />
                    </div>
                     <div className={styles.buttons}>
                        <div className={styles.leftContent}>
                            {/* Empty for now */}
                        </div>
                        <div className={styles.rightContent}>
                            {/* The copy button was moved to the top bar */}
                        </div>
                    </div>
                </div>
            </div>
            {/* RESIZE SLIDER */}
			<div ref={resizeHandleRef} className={styles.resizeHandle} onMouseDown={handleMouseDown} />
            {error && <p className={styles.errorMessage}>{error}</p>}
        </div>
    );
}; 